import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
	Box,
	Button,
	Checkbox,
	CircularProgress,
	Divider,
	FormControlLabel,
	Grid,
	Typography,
} from '@mui/material';
import { LocalizationProvider, TimePicker } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import { Formik } from 'formik';
import { useQuery, useQueryClient } from 'react-query';
import { User } from 'types';
import { getDays } from 'core/API/organizationSettings';
import { getJobCodes } from 'core/API/scheduler';
import {
	getJobCodeIdFromUserJobCode,
	getUserJobCodes,
	syncUserJobCodes,
} from 'core/API/userJobCodes';
import { apiService } from 'core/services/apiService';
import { useToastContext } from 'core/components/Toast/context/ToastContext';
import { useWorkplacesContext } from 'module/workplaces/context/WorkplacesContext';
import EmployeeRequestModal from 'module/schedule/components/EmployeeRequestModal/EmployeeRequestModal';
import EligibleJobCodesPicker from 'module/schedule/components/EligibleJobCodesPicker/EligibleJobCodesPicker';
import { hasAvailabilityDayChanges } from 'module/schedule/components/EmployeeRequestModal/forms/AvailabilityForm';
import { buildAllWeekDays } from 'module/schedule/utils/availabilityDisplay';
import {
	buildOnboardingAvailabilityInitialValues,
	extractUserAvailabilityFromUser,
	OnboardingDayAvailability,
	saveOnboardingAvailability,
} from 'module/onboarding/utils/onboardingAvailability';
import styles from './EditUserSettings.module.scss';

interface UserAvailabilityManagementProps {
	user: User;
	/** When true, job codes are editable (manager editing another user). */
	canManageJobCodes: boolean;
	/**
	 * When true (editing own settings), availability changes are submitted as
	 * shift requests. Managers editing others save availability directly.
	 */
	saveAsRequest: boolean;
	onBack: () => void;
}

const sameIdSet = (a: number[], b: number[]): boolean => {
	if (a.length !== b.length) return false;
	const sortedA = [...a].sort((x, y) => x - y);
	const sortedB = [...b].sort((x, y) => x - y);
	return sortedA.every((id, index) => id === sortedB[index]);
};

const UserAvailabilityManagement: React.FC<UserAvailabilityManagementProps> = ({
	user,
	canManageJobCodes,
	saveAsRequest,
	onBack,
}) => {
	const { showToast } = useToastContext();
	const queryClient = useQueryClient();
	const { workplaceId } = useWorkplacesContext();
	const availabilityFormRef = useRef<any>(null);
	const initialAvailabilityRef = useRef<OnboardingDayAvailability[] | null>(null);
	const [selectedIds, setSelectedIds] = useState<number[]>([]);
	const [initialSelectedIds, setInitialSelectedIds] = useState<number[]>([]);
	const [isSaving, setIsSaving] = useState(false);
	const [jobCodesInitialized, setJobCodesInitialized] = useState(false);
	const [availabilitySnapshotReady, setAvailabilitySnapshotReady] = useState(false);

	const { data: daysData, isLoading: daysLoading } = useQuery(['days'], () => getDays(), {
		enabled: !saveAsRequest,
	});
	const allDays = useMemo(() => buildAllWeekDays(daysData), [daysData]);

	const { data: jobCodes = [], isLoading: jobCodesLoading } = useQuery(
		'jobCodes',
		() => getJobCodes(),
		{ staleTime: 5 * 60 * 1000 }
	);

	const {
		data: userJobCodes = [],
		isLoading: userJobCodesLoading,
		refetch: refetchUserJobCodes,
	} = useQuery(
		['user-job-codes', user.id],
		() => getUserJobCodes({ userId: user.id }),
		{ staleTime: 30 * 1000 }
	);

	const {
		data: userData,
		isLoading: userLoading,
		refetch: refetchUser,
	} = useQuery(
		['user-availability', user.id, 'settings'],
		async () => {
			const response = await apiService.get(`/users/${user.id}`);
			return apiService.responseHandler(response);
		},
		{ refetchOnMount: 'always', staleTime: 0, enabled: !saveAsRequest }
	);

	const currentAvailability = useMemo(
		() => extractUserAvailabilityFromUser(userData),
		[userData]
	);

	const initialValues = useMemo(
		() => buildOnboardingAvailabilityInitialValues(allDays, currentAvailability),
		[allDays, currentAvailability]
	);

	useEffect(() => {
		if (jobCodesInitialized || userJobCodesLoading) return;
		const ids = userJobCodes
			.map((row) => getJobCodeIdFromUserJobCode(row))
			.filter((id): id is number => id != null);
		setSelectedIds(ids);
		setInitialSelectedIds(ids);
		setJobCodesInitialized(true);
	}, [userJobCodes, userJobCodesLoading, jobCodesInitialized]);

	useEffect(() => {
		if (saveAsRequest) return;
		if (daysLoading || userLoading || initialValues.availability.length === 0) return;
		initialAvailabilityRef.current = JSON.parse(JSON.stringify(initialValues.availability));
		setAvailabilitySnapshotReady(true);
	}, [initialValues, daysLoading, userLoading, saveAsRequest]);

	const jobCodesChanged = useMemo(
		() => canManageJobCodes && !sameIdSet(selectedIds, initialSelectedIds),
		[canManageJobCodes, selectedIds, initialSelectedIds]
	);

	const handleDirectSave = async (availability: OnboardingDayAvailability[]) => {
		const availabilityChanged = hasAvailabilityDayChanges(
			availability,
			initialAvailabilityRef.current || undefined
		);

		if (!jobCodesChanged && !availabilityChanged) {
			showToast('success', 'No changes to save');
			return;
		}

		if (!workplaceId) {
			showToast('error', 'Workplace is required to save availability');
			return;
		}

		const enabledDays = availability.filter((d) => d.enabled);
		if (availabilityChanged && enabledDays.length === 0) {
			showToast('error', 'Please select at least one day the employee is available');
			return;
		}

		const invalidDays = availability.filter((d) => d.enabled && (!d.startTime || !d.endTime));
		if (invalidDays.length > 0) {
			const dayNames = invalidDays.map((d) => d.day.label).join(', ');
			showToast('error', `Please set start and end times for: ${dayNames}`);
			return;
		}

		setIsSaving(true);
		const savedParts: string[] = [];

		try {
			if (jobCodesChanged) {
				await syncUserJobCodes(user.id, selectedIds);
				await refetchUserJobCodes();
				queryClient.invalidateQueries(['user-job-codes']);
				setInitialSelectedIds([...selectedIds]);
				savedParts.push('job codes');
			}

			if (availabilityChanged) {
				await saveOnboardingAvailability({
					workplaceId,
					days: availability,
					currentAvailability,
					userId: user.id,
				});
				await queryClient.invalidateQueries(['user-availability']);
				await refetchUser();
				initialAvailabilityRef.current = JSON.parse(JSON.stringify(availability));
				savedParts.push('availability');
			}

			if (savedParts.length === 1 && savedParts[0] === 'job codes') {
				showToast('success', 'Eligible job codes saved');
			} else if (savedParts.length === 1 && savedParts[0] === 'availability') {
				showToast('success', 'Availability saved');
			} else if (savedParts.length > 1) {
				showToast('success', 'Job codes and availability saved');
			}

			if (savedParts.length > 0) {
				onBack();
			}
		} catch (error: any) {
			if (savedParts.length > 0) {
				showToast(
					'success',
					`Saved ${savedParts.join(' and ')}. Remaining changes were not saved.`
				);
			} else {
				showToast('error', error?.message || 'Failed to save changes');
			}
		} finally {
			setIsSaving(false);
		}
	};

	const handleRequestSave = async () => {
		const availabilityChanged = Boolean(
			availabilityFormRef.current?.hasAvailabilityChanges?.()
		);

		if (!availabilityChanged) {
			showToast('success', 'No changes to save');
			return;
		}

		setIsSaving(true);
		try {
			const form = availabilityFormRef.current;
			if (!form?.submitForm) {
				throw new Error('Availability form is not ready');
			}

			await form.submitForm();

			const hasErrors =
				form.errors && Object.values(form.errors).some((value) => Boolean(value));

			if (hasErrors) {
				return;
			}

			showToast('success', 'Availability request submitted');
			onBack();
		} catch (error: any) {
			if (!error?.isFormValidation) {
				showToast('error', error?.message || 'Failed to submit availability request');
			}
		} finally {
			setIsSaving(false);
		}
	};

	const isLoading =
		jobCodesLoading ||
		userJobCodesLoading ||
		!jobCodesInitialized ||
		(!saveAsRequest &&
			(daysLoading || userLoading || !availabilitySnapshotReady));

	const jobCodesSection = (
		<>
			{isLoading && !saveAsRequest ? (
				<Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
					<CircularProgress size={28} />
				</Box>
			) : (
				<>
					{(jobCodesLoading || userJobCodesLoading || !jobCodesInitialized) && saveAsRequest ? (
						<Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
							<CircularProgress size={28} />
						</Box>
					) : (
						<EligibleJobCodesPicker
							jobCodes={jobCodes}
							selectedIds={selectedIds}
							onChange={setSelectedIds}
							disabled={!canManageJobCodes || isSaving}
							helperText={
								canManageJobCodes
									? 'Select the Job Code(s) this employee is eligible to work.'
									: 'Job Codes assigned by your manager (read-only).'
							}
						/>
					)}
					{!canManageJobCodes && selectedIds.length === 0 && !userJobCodesLoading && (
						<Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
							No Job Codes have been assigned yet.
						</Typography>
					)}
				</>
			)}
		</>
	);

	if (saveAsRequest) {
		return (
			<div className={styles.editUserSetting}>
				<Box
					sx={{
						display: 'flex',
						justifyContent: 'space-between',
						alignItems: 'center',
						mb: 2,
						px: 2,
					}}
				>
					<Button className={styles.backButton} onClick={onBack} disabled={isSaving}>
						Back
					</Button>
					<Button
						className={styles.saveButton}
						onClick={handleRequestSave}
						disabled={isSaving || isLoading}
					>
						{isSaving ? 'Submitting...' : 'Submit Request'}
					</Button>
				</Box>

				<Box sx={{ px: 2, pb: 2 }}>
					<Typography variant="h6" sx={{ mb: 1 }}>
						Availability — {user.firstName} {user.lastName}
					</Typography>
					<Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
						Request a change to your weekly availability. A manager must approve the request
						before it takes effect. Job Codes are assigned by your manager.
					</Typography>

					{jobCodesSection}
				</Box>

				<Divider sx={{ mx: 2, my: 2 }} />

				<Box sx={{ px: 2, pb: 3 }}>
					<EmployeeRequestModal
						embedded
						formRef={availabilityFormRef}
						closeModal={onBack}
						refetchSchedules={() => undefined}
						defaultRequestType="availability"
						hideRequestTypeSelector
						userId={user.id}
					/>
				</Box>
			</div>
		);
	}

	return (
		<div className={styles.editUserSetting}>
			<Formik
				initialValues={initialValues}
				enableReinitialize
				onSubmit={async (values) => {
					await handleDirectSave(values.availability);
				}}
			>
				{({ values, setFieldValue, handleSubmit: formikSubmit }) => (
					<>
						<Box
							sx={{
								display: 'flex',
								justifyContent: 'space-between',
								alignItems: 'center',
								mb: 2,
								px: 2,
							}}
						>
							<Button className={styles.backButton} onClick={onBack} disabled={isSaving}>
								Back
							</Button>
							<Button
								className={styles.saveButton}
								onClick={() => formikSubmit()}
								disabled={isSaving || isLoading}
							>
								{isSaving ? 'Saving...' : 'Save'}
							</Button>
						</Box>

						<Box sx={{ px: 2, pb: 2 }}>
							<Typography variant="h6" sx={{ mb: 1 }}>
								Availability — {user.firstName} {user.lastName}
							</Typography>
							<Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
								Update Job Code eligibility and weekly availability directly. At least one Job
								Code is required for the employee to appear on the schedule.
							</Typography>

							{isLoading ? (
								<Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
									<CircularProgress size={28} />
								</Box>
							) : (
								<>
									{jobCodesSection}

									<Divider sx={{ my: 2 }} />

									<Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600 }}>
										Weekly Availability
									</Typography>
									<Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
										Changes are saved directly to the employee&apos;s availability (no approval
										request).
									</Typography>

									<LocalizationProvider dateAdapter={AdapterDayjs}>
										{values.availability.map((d, index) => (
											<Grid
												container
												spacing={2}
												alignItems="center"
												key={d.day['@id'] || d.day.id}
												sx={{ mb: 1, borderRadius: 1, p: 1 }}
											>
												<Grid item xs={12} sm={4}>
													<FormControlLabel
														control={
															<Checkbox
																checked={d.enabled}
																disabled={isSaving}
																onChange={(e) => {
																	const updated = [...values.availability];
																	updated[index] = {
																		...updated[index],
																		enabled: e.target.checked,
																	};
																	if (!e.target.checked) {
																		updated[index].startTime = null;
																		updated[index].endTime = null;
																	} else {
																		const lastFilledDay = [...updated]
																			.slice(0, index)
																			.reverse()
																			.find(
																				(day) =>
																					day.enabled && day.startTime && day.endTime
																			);
																		if (lastFilledDay) {
																			updated[index].startTime = dayjs(
																				lastFilledDay.startTime
																			);
																			updated[index].endTime = dayjs(lastFilledDay.endTime);
																		}
																	}
																	setFieldValue('availability', updated);
																}}
															/>
														}
														label={d.day.label}
													/>
												</Grid>
												<Grid item xs={6} sm={4}>
													<TimePicker
														label="Start Time"
														ampm
														format="hh:mm A"
														value={d.startTime}
														disabled={!d.enabled || isSaving}
														onChange={(newTime) => {
															const updated = [...values.availability];
															updated[index] = {
																...updated[index],
																startTime: newTime,
															};
															setFieldValue('availability', updated);
														}}
														slotProps={{ textField: { fullWidth: true } }}
													/>
												</Grid>
												<Grid item xs={6} sm={4}>
													<TimePicker
														label="End Time"
														ampm
														format="hh:mm A"
														value={d.endTime}
														disabled={!d.enabled || isSaving}
														onChange={(newTime) => {
															const updated = [...values.availability];
															updated[index] = {
																...updated[index],
																endTime: newTime,
															};
															setFieldValue('availability', updated);
														}}
														slotProps={{ textField: { fullWidth: true } }}
													/>
												</Grid>
											</Grid>
										))}
									</LocalizationProvider>
								</>
							)}
						</Box>
					</>
				)}
			</Formik>
		</div>
	);
};

export default UserAvailabilityManagement;
